<template>
  <div class="quizes-table-wrapper">
    <div class="d-flex flex-row align-items-center justify-content-between py-3">
      <small>{{ total }} quizes found</small>
    </div>
    <b-table
      id="quizesTable"
      ref="table"
      :busy.sync="busy"
      :items="quizesProvider"
      primary-key="id"
      responsive="sm"
      :dark="isDarkThemeEnabled"
      :fields="fields"
      :filter="tableFilters"
      :per-page="15"
      :current-page="currentPage"
    >
      <template #cell(title)="data">
        <div>
          <b-link class="project-title">
            <h6>{{ data.item.title }}</h6>
          </b-link>
        </div>
        <div>
          <quiz-state-tag :state="data.item.state" />
          <btn
            v-if="data.item.state === 'open' && data.item.caseGroups && data.item.caseGroups[0]"
            v-b-tooltip.hover
            variant="light"
            size="xs"
            icon-right
            icon-name="fa-angle-right"
            :btn-text="$t('cmrad.take_quiz')"
            :title="$t('cmrad.take_this_quiz')"
            :href="
              $route('group.quizzes.get', {
                caseGroup: data.item.caseGroups[0],
                quiz: data.item.id,
              })
            "
          />
        </div>
      </template>

      <template #cell(actions)="data">
        <div class="actions" v-if="data.item.caseGroups[0]">
          <btn
            v-b-tooltip.hover
            fab
            icon
            icon-name="fa-cog"
            size="lg"
            class="mr-2"
            variant="light"
            tag="inertia-link"
            :title="$t('cmrad.configure_quiz')"
            :href="
              $route('group.quizzes.configure', {
                caseGroup: data.item.caseGroups[0],
                quiz: data.item.id,
              })
            "
          >
            <span class="sr-only">Configure</span>
            <!--  -->
          </btn>
          <btn
            v-b-tooltip.hover
            fab
            icon
            icon-name="fa-edit"
            size="lg"
            class="mr-2"
            variant="light"
            tag="inertia-link"
            :title="$t('cmrad.rename_quiz')"
            :href="
              $route('group.quizzes.edit', {
                caseGroup: data.item.caseGroups[0],
                quiz: data.item.id,
              })
            "
          >
            <span class="sr-only">Edit quiz</span>
          </btn>
          <drop-down-menu right lazy nocaret boundary="window">
            <btn
              slot="dropDownIcon"
              v-b-tooltip.hover
              fab
              icon
              icon-name="fa-ellipsis-h"
              size="lg"
              variant="light"
              :title="$t('cmrad.quiz_actions')"
            />

            <fragment slot="dropDownContent">
              <b-dropdown-item
                :href="
                  $route('groups.quizzes.results', {
                    caseGroup: data.item.caseGroups[0],
                    id: data.item.id,
                    version: '1.0',
                  })
                "
              >
                {{ $t('cmrad.download_results') }}
              </b-dropdown-item>
              <b-dropdown-divider />
              <b-dropdown-item variant="danger" @click="onDelete($event, data.item)">
                {{ $t('cmrad.delete_quiz') }}
              </b-dropdown-item>
            </fragment>
          </drop-down-menu>
        </div>

        <!-- <quiz-update-modal :id="getModalId(data.item)" :quiz-data="formData(data.item)" @updated="onQuizUpdated" /> -->
      </template>

      <template #cell(lifetime)="data">
        <fragment v-if="data.item.starts_at">
          <p>{{ getDurationLength(data.item) }}</p>
          Starting at {{ formatDate(data.item.starts_at) }}
        </fragment>
        <p v-else class="text-muted">Undefined dates</p>
      </template>

      <template #cell(completed_pct)="data">
        <b-progress variant="success" :value="data.item.completed_pct" show-progress />
      </template>
    </b-table>

    <div class="d-flex flex-row align-items-center justify-content-between">
      <b-pagination v-model="currentPage" class="my-3" :total-rows="total" :per-page="10" aria-controls="usersTable" />
    </div>

    <loading-modal v-model="deleting">Deleting quiz...</loading-modal>
    <loading-modal v-model="busy" />
    <loading-modal v-model="transitioning">Applying state transition</loading-modal>
  </div>
</template>

<script>
  import { mapGetters } from 'vuex';
  import { format, formatDistance, parseISO } from 'date-fns';
  import toastr from 'toastr';
  import { sentence } from 'case';

  export default {
    name: 'QuizesTableNew',

    components: {
      QuizCreateModal: () => import('@/Organisms/Admin/QuizCreateModal'),
      QuizStateTag: () => import('@/Atoms/QuizStateTag'),
      QuizUpdateModal: () => import('@/Organisms/Admin/QuizUpdateModal'),
      LoadingModal: () => import('@/Atoms/LoadingModal'),
      DropDownMenu: () => import('@/Molecules/DropDownMenu'),
    },

    data() {
      return {
        busy: false,
        currentPage: 1,
        total: 0,
        deleting: false,
        transitioning: false,
      };
    },

    props: {
      quizes: Array,
      filters: { type: Object, default: () => ({}) },
    },

    computed: {
      ...mapGetters(['isDarkThemeEnabled']),

      fields() {
        return [
          { key: 'title', sortable: true },
          { key: 'createdAt', sortable: true, formatter: (value) => this.formatDate(value) },
          { key: 'caseCount', sortable: true, label: 'Cases' },
          { key: 'caseGroupsTitle', sortable: true, label: 'Groups', formatter: (value) => this.formatTitle(value) },
          // { key: 'lifetime', sortable: false },
          { key: 'actions' },
        ];
      },
      //+ For this IT1 we remove Create Quiz Button
      // +See active answer button => change copy to “Take Quiz”
      // +Remove Users Column
      // +Remove Progress Column
      // ??? Add group column that show for which group the Quiz is shared with
      // + Lifetime is a feature to be developed in IT2, we can remove for this IT1

      tableFilters() {
        let filters = {};

        for (const key in this.filters) {
          if (this.filters[key]) {
            filters = Object.assign({}, filters, { [key]: this.filters[key] });
          }
        }

        return filters;
      },
    },

    methods: {
      onQuizUpdated() {
        this.refreshData();
      },

      quizesProvider(ctx) {
        const params = Object.assign({}, { limit: 10, page: this.currentPage }, ctx);
        const promise = this.$api('admin.quizzes.index', params);

        return promise
          .then(({ data }) => {
            const items = data.data;
            this.total = data.meta.total;

            return items || [];
          })
          .catch((err) => {
            toastr.error('Unable to fetch data');
            console.error(err);

            return [];
          });
      },

      refreshData() {
        this.$refs.table.refresh();
      },

      formatTitle(groupArray) {
        return groupArray?.map((item) => item.title).join(', ');
      },

      formatDate(dateStr) {
        return format(this.parseDate(dateStr), 'dd/MM/yyyy HH:mm:ss');
      },

      parseDate(dateStr) {
        return new Date(parseISO(dateStr));
      },

      async onDelete(evt, data) {
        const msg = `Are you sure you want to delete the quiz ${data.title}? This action cannot be undone!`;
        const confirmed = await this.$bvModal.msgBoxConfirm(msg, {
          title: 'Please Confirm',
          okTitle: 'Yes, I am sure, proceed',
          cancelTitle: 'No, cancel operation',
          cancelVariant: 'link text-danger',
          autoFocusButton: 'ok',
          footerClass: 'p-2',
          hideHeaderClose: false,
          centered: true,
        });

        if (confirmed) {
          this.deleting = true;

          await this.$api('groups.quizzes.delete', {
            caseGroup: data.caseGroups[0],
            id: data.id,
          });

          this.$nextTick(() => {
            this.deleting = false;
            this.refreshData();
          });
        }
      },

      getModalId({ id }) {
        return `quiz-modal-${id}`;
      },

      formData(quiz) {
        let users = null;
        let cases = null;

        if (quiz.users && quiz.users.length) {
          users = quiz.users.map(({ id }) => id);
        }

        if (quiz.cases && quiz.cases.length) {
          cases = quiz.cases.map(({ location, title, studies }) => ({
            location,
            title,
            studies: studies.map(({ id }) => id),
          }));
        }

        return {
          id: quiz.id,
          slug: quiz.slug,
          title: quiz.title,
          description: quiz.description,
          dates: {
            starts_at: this.parseDate(quiz.starts_at),
            ends_at: this.parseDate(quiz.ends_at),
          },
          cases,
          users,
        };
      },

      getDurationLength(quiz) {
        if (!quiz.ends_at) {
          return 'Undefined length';
        }

        return formatDistance(this.parseDate(quiz.starts_at), this.parseDate(quiz.ends_at));
      },

      // async applyTransition(quiz, transition) {
      //   const url = this.$route('apply-quiz-transition', { quiz: quiz.slug });

      //   this.transitioning = true;
      //   await axios.post(url, { transition });

      //   this.$nextTick(() => {
      //     this.transitioning = false;
      //     this.refreshData();
      //   });
      // },

      convertCaseForTransition(transition) {
        return sentence(transition);
      },
    },
  };
</script>
<style lang="scss" scoped>
  ::v-deep {
    table td {
      .actions {
        white-space: nowrap;
      }

      &[aria-colindex='7'] {
        padding: 0.75rem 0.3rem;
      }
    }

    .project-title:hover {
      color: inherit;
    }
  }
</style>
